import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenWithIcon from "@mui/icons-material/OpenWith";
import {
  Box,
  capitalize,
  Collapse,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import { styled } from "@mui/system";
import React, { useCallback, useEffect, useRef, useState } from "react";

import useAPI from "../../../../../../../api/hooks/useAPI";
import useColors from "../../../../../../../api/hooks/useColors";
import CustomToolTip from "../../../../../../../components/CustomTooltip";
import { Toast } from "../../../../../../../components/Toast";
import SvgToIcon from "../../../../../../../icons/SvgToIcon";
import {
  sanitizeSecrets,
  sanitizeXml,
} from "../../../../../../../utils/sanitizeSecrets";
import { extractXmlContent } from "../../../../../../../utils/xmlFormatter";
import { icons } from "../../../../../../stack/environment/icons/iconExport";

// Styled components
export const ExpandMore = styled((props) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  transform: !expand ? "rotate(0deg)" : "rotate(180deg)",
  marginLeft: "auto",
  transition: theme.transitions.create("transform", {
    duration: theme.transitions.duration.shortest,
  }),
}));

// Custom hooks
export const useResizing = (initialDimensions) => {
  const [dimensions, setDimensions] = useState(initialDimensions);
  const [isResizing, setIsResizing] = useState({ active: false, type: null });
  const initialMousePos = useRef({ x: 0, y: 0 });

  const handleMouseDown = useCallback((e, type) => {
    setIsResizing({ active: true, type });
    initialMousePos.current = { x: e.clientX, y: e.clientY };
  }, []);

  const handleMouseMove = useCallback(
    (e) => {
      if (!isResizing.active) return;

      const deltaX = e.clientX - initialMousePos.current.x;
      const deltaY = e.clientY - initialMousePos.current.y;

      setDimensions((prev) => ({
        width: Math.max(
          400,
          prev.width + (isResizing.type !== "vertical" ? deltaX * 2 : 0)
        ),
        height: Math.max(
          300,
          prev.height + (isResizing.type !== "horizontal" ? deltaY * 2 : 0)
        ),
      }));

      initialMousePos.current = { x: e.clientX, y: e.clientY };
    },
    [isResizing]
  );

  const handleMouseUp = useCallback(() => {
    setIsResizing({ active: false, type: null });
  }, []);

  return {
    dimensions,
    isResizing,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
  };
};

export const useCodeBlockResize = (initialHeight = 220) => {
  const [height, setHeight] = useState(initialHeight);
  const [isResizing, setIsResizing] = useState(false);
  const initialMousePos = useRef({ y: 0 });

  const handleMouseDown = useCallback((e) => {
    setIsResizing(true);
    initialMousePos.current = { y: e.clientY };
    e.preventDefault();
  }, []);

  const handleMouseMove = useCallback(
    (e) => {
      if (!isResizing) return;

      const deltaY = e.clientY - initialMousePos.current.y;
      setHeight((prev) => Math.max(80, prev + deltaY));
      initialMousePos.current = { y: e.clientY };
    },
    [isResizing]
  );

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    } else {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    }
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  return { height, handleMouseDown };
};

// Components
export const CodeBlock = ({
  title,
  content,
  expandable = true,
  controlId,
  configFormat,
  initialHeight = 220,
}) => {
  const colors = useColors();
  const { trackEvent } = useAPI();
  const [expanded, setExpanded] = useState(true);
  const { height, handleMouseDown } = useCodeBlockResize(initialHeight);

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const formatData = (data) => {
    if (configFormat === "xml") {
      return sanitizeXml(extractXmlContent(data));
    }
    return JSON.stringify(sanitizeSecrets(data), null, 2);
  };

  const handleCopy = () => {
    trackEvent(`User copied ${title} information`).then();
    navigator.clipboard.writeText(formatData(content));
    Toast.success(`${capitalize(title)} information copied to clipboard!`);
  };

  const handleDownload = () => {
    trackEvent(`User downloaded ${title}`).then();
    const mimeType =
      configFormat === "xml" ? "application/xml" : "application/json";
    const blob = new Blob([formatData(content)], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `gap_${controlId}_${title.toLowerCase()}`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <SvgToIcon
            IconLogo={
              title === "Evidence"
                ? icons.evidenceIcon
                : icons.recommendedConfiguration
            }
            width={"20px"}
            style={{ marginRight: "8px", color: colors.common_white }}
          />
          <Typography variant="subtitle1">{title}</Typography>
        </Box>
        <Box display="flex" alignItems="center">
          <ActionButton
            tooltip={`Copy the ${title.toLowerCase()}`}
            onClick={handleCopy}
            icon={<ContentCopyRoundedIcon sx={{ fontSize: "16px" }} />}
          />
          <ActionButton
            tooltip={`Download the ${title.toLowerCase()}`}
            onClick={handleDownload}
            icon={<DownloadRoundedIcon sx={{ fontSize: "16px" }} />}
            sx={{ mr: expandable ? 1 : 0 }}
          />
          {expandable && (
            <ExpandMore
              expand={expanded}
              onClick={handleExpandClick}
              aria-expanded={expanded}
              aria-label="show more"
              sx={{
                border: "1px solid " + colors.divider2,
                borderRadius: "8px",
                padding: "4px",
                width: "28px",
                height: "28px",
              }}
            >
              <ExpandMoreIcon sx={{ fontSize: "16px" }} />
            </ExpandMore>
          )}
        </Box>
      </Box>
      <Collapse
        in={expanded || !expandable}
        timeout="auto"
        unmountOnExit={false}
      >
        <Box
          sx={{
            position: "relative",
          }}
        >
          <Paper
            sx={{
              padding: "16px",
              paddingBottom: expanded ? "24px" : "16px", // Extra space for resize handle
              backgroundColor: colors.grey_8percent,
              whiteSpace: "pre-wrap",
              fontFamily: "Menlo",
              fontWeight: "400",
              borderRadius: "6px",
              fontSize: "12px",
              boxShadow: "none",
              border: `1px solid ${colors.divider2}`,
              height: expanded ? `${height}px` : "120px",
              overflow: "auto",
              transition: "border-color 0.2s ease",
              "&:hover": {
                borderColor: expanded ? colors.primary + "40" : colors.divider2,
              },
              position: "relative",
            }}
          >
            {formatData(content)}
          </Paper>
          {expanded && <ResizeHandle onMouseDown={handleMouseDown} />}
        </Box>
      </Collapse>
    </Box>
  );
};

export const ActionButton = ({ tooltip, onClick, icon, sx = {} }) => {
  const colors = useColors();

  return (
    <Box
      sx={{
        border: "1px solid " + colors.divider2,
        borderRadius: "8px",
        padding: "4px",
        width: "28px",
        height: "28px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        mr: 1,
        ...sx,
      }}
    >
      <CustomToolTip title={tooltip}>
        <IconButton aria-label={tooltip} onClick={onClick} size="small">
          {React.cloneElement(icon, {
            sx: { color: colors.common_black, ...icon.props.sx },
          })}
        </IconButton>
      </CustomToolTip>
    </Box>
  );
};

export const ResizeHandle = ({ onMouseDown }) => {
  const colors = useColors();

  return (
    <CustomToolTip title="Drag to resize" placement="top">
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: "50%",
          transform: "translateX(-50%)",
          width: "40px",
          height: "20px",
          cursor: "ns-resize",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: colors.divider2,
          borderRadius: "10px 10px 0 0",
          "&:after": {
            content: '""',
            width: "30px",
            height: "6px",
            backgroundColor: colors.grey_500,
            borderRadius: "2px",
          },
          zIndex: 10,
        }}
        onMouseDown={onMouseDown}
      ></Box>
    </CustomToolTip>
  );
};

export const ExpandableEvidenceView = ({ evidence, controlId }) => {
  const [evidenceConfig, setEvidenceConfig] = useState(null);
  const [recommendedConfig, setRecommendedConfig] = useState(null);
  const [recommendedConfigFormat, setRecommendedConfigFormat] = useState(null);
  const [evidenceConfigFormat, setEvidenceConfigFormat] = useState(null);

  useEffect(() => {
    if (evidence) {
      try {
        const parsedEvidence = JSON.parse(evidence);
        setRecommendedConfigFormat(
          parsedEvidence.recommended_config_format || "json"
        );
        setEvidenceConfigFormat(parsedEvidence.config_format || "json");

        setEvidenceConfig(parsedEvidence?.config || parsedEvidence);
        setRecommendedConfig(parsedEvidence?.recommended_config);
      } catch (e) {
        setEvidenceConfig(evidence);
      }
    }
  }, [evidence]);

  return (
    <Box>
      {evidenceConfig && (
        <CodeBlock
          title="Evidence"
          content={evidenceConfig}
          expandable={true}
          controlId={controlId}
          configFormat={evidenceConfigFormat}
          initialHeight={recommendedConfig ? 220 : 500}
        />
      )}
      {recommendedConfig && (
        <CodeBlock
          title="Recommended Configuration"
          content={recommendedConfig}
          expandable={true}
          controlId={controlId}
          configFormat={recommendedConfigFormat}
        />
      )}
    </Box>
  );
};

export const ModalResizeHandle = ({ type, position, onMouseDown }) => {
  const colors = useColors();

  return (
    <IconButton
      sx={{
        position: "absolute",
        ...position,
        backgroundColor: colors.grey_8percent,
        "&:hover": {
          backgroundColor: colors.divider2,
        },
        cursor: "move",
        padding: "4px",
        minWidth: "28px",
        minHeight: "28px",
      }}
      onMouseDown={(e) => onMouseDown(e, type)}
    >
      <CustomToolTip title="Drag to resize" placement="top">
        <OpenWithIcon fontSize="small" />
      </CustomToolTip>
    </IconButton>
  );
};
